import { useEffect, useRef, useState } from 'react'
import { sendLoginCode, signInWithCode, signInWithGoogle } from '../api/auth'
import { isValidEmail, normalizeEmail } from '../api/emailAddress'
import { takeAccountDeletedFlag } from '../storage'
import GoogleIcon from './icons/GoogleIcon'
import ScorelineIcon from './icons/ScorelineIcon'
import './LoginScreen.css'

/** Shown instead of the app whenever there's no signed-in session — there's no anonymous/guest
 * mode, so this is the only way in. Two ways through it: a 6-digit code emailed to
 * whatever address the user types, or Google. Both land on the same account when the address is
 * the same one. */

/** Footer mark size (px). Smaller than the bottom nav's 33 — down here it signs the page, it
 * doesn't have to be tappable. Passed as a prop rather than set in CSS so the glyph's stroke
 * compensation (see scorelineGeometry) is computed for the size it actually renders at. */
const FOOTER_MARK_SIZE = 22

const CODE_LENGTH = 6
/** One index per display cell — the cells are presentational, the input behind them is the field. */
const CODE_CELLS = Array.from({ length: CODE_LENGTH }, (_, index) => index)
/** Long enough that an impatient user doesn't burn the server's send limit before the first mail
 * has even arrived. */
const RESEND_COOLDOWN_SECONDS = 30

interface AuthFailure {
  code?: string
  status?: number
}

/** Distinct copy per failure — "Failed" tells the user nothing about what to do next. */
function sendFailureMessage(error: AuthFailure): string {
  if (error.status === 429) return 'Too many codes requested. Wait a minute, then try again.'
  return "Couldn't send the code. Check the address and try again."
}

function codeFailureMessage(error: AuthFailure): string {
  switch (error.code) {
    case 'INVALID_OTP':
      return "That code doesn't match. Check the digits and try again."
    case 'OTP_EXPIRED':
      return 'That code has expired. Send yourself a new one.'
    case 'TOO_MANY_ATTEMPTS':
      return 'Too many wrong codes. Send yourself a new one.'
    default:
      return error.status === 429
        ? 'Too many attempts. Wait a minute, then try again.'
        : "Couldn't sign you in with that code. Send yourself a new one."
  }
}

function LoginScreen() {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  /** Which code is already in flight (or has been sent). Autofill fires an input event and the
   * user may hit the button on top of it — without this the same code is verified twice, and the
   * second attempt burns one of the three allowed tries against a code that just worked. */
  const submittedCode = useRef<string | null>(null)
  /**
   * A self-serve deletion ends here, after a reload, and a bare login form leaves
   * the user wondering whether it worked. Read once in the initialiser — `takeAccountDeletedFlag`
   * clears the flag as it reads it, so the message shows for this render and never returns.
   */
  const [accountDeleted] = useState(takeAccountDeletedFlag)

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const send = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const { error: sendError } = await sendLoginCode(email)
      if (sendError) {
        setError(sendFailureMessage(sendError))
        return
      }
      submittedCode.current = null
      setCode('')
      setStep('code')
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch {
      setError(sendFailureMessage({}))
    } finally {
      setBusy(false)
    }
  }

  const verify = async (candidate: string) => {
    if (busy || submittedCode.current === candidate) return
    submittedCode.current = candidate
    setBusy(true)
    setError(null)
    try {
      const { error: signInError } = await signInWithCode(email, candidate)
      if (signInError) {
        setError(codeFailureMessage(signInError))
        // A rejected code may legitimately be retyped — only a code still in flight is blocked.
        submittedCode.current = null
        return
      }
      // The session is read once, at mount (see useSessionBootstrap), so the cookie alone changes
      // nothing on screen — Google gets its reload for free by navigating back from the consent
      // screen, and this flow never leaves the tab. Reload so the same sign-in gate runs.
      window.location.reload()
    } catch {
      setError(codeFailureMessage({}))
      submittedCode.current = null
    } finally {
      setBusy(false)
    }
  }

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(digits)
    // The rejection was about the digits that were there a keystroke ago — keeping the red cells
    // and the message up while the user retypes says the new code is wrong too, before it's sent.
    setError(null)
    // Auto-submit on the last digit, so autofill reads as "tap the suggestion, you're in". The
    // button stays for screen readers and for a paste that arrives with a stray character.
    if (digits.length === CODE_LENGTH) void verify(digits)
  }

  const handleGoogle = async () => {
    setError(null)
    try {
      const { error: signInError } = await signInWithGoogle()
      if (signInError) setError('Failed to sign in with Google. Please try again.')
    } catch {
      setError('Failed to sign in with Google. Please try again.')
    }
  }

  const useDifferentEmail = () => {
    submittedCode.current = null
    setStep('email')
    setCode('')
    setError(null)
  }

  /** The cell the next digit lands in. Clamped so a full code keeps the caret on the last cell
   * rather than pointing past the row. */
  const activeCell = Math.min(code.length, CODE_LENGTH - 1)

  return (
    <div className="login-screen">
      {/* Its own block, above the swapping body, so the wordmark and subtitle sit at the same
          height in both steps instead of being re-centred against whatever the step is tall. */}
      <header className="login-screen__header">
        {/* Uppercase in the markup, not via text-transform: this is the name, not a label the
            design system is shouting (and component stylesheets may not declare uppercase — see
            designSystem.test.ts). Two lines because the lockup is two lines. */}
        <p className="login-screen__title">
          <span className="login-screen__title-line login-screen__title-line--respect">
            RESPECT
          </span>
          <span className="login-screen__title-line">FOOTBALL</span>
        </p>
        <p className="login-screen__subtitle">Predict every week</p>
      </header>

      {accountDeleted && (
        <p className="login-screen__notice" role="status">
          Your account and all its data were deleted.
        </p>
      )}

      {step === 'email' ? (
        <div className="login-screen__stack">
          <button type="button" className="login-screen__button" onClick={handleGoogle}>
            <GoogleIcon className="login-screen__button-icon" />
            Sign in with Google
          </button>

          <div className="login-screen__divider">
            <span className="login-screen__divider-rule" />
            <span className="login-screen__divider-label">or</span>
            <span className="login-screen__divider-rule" />
          </div>

          <form
            className="login-screen__form"
            onSubmit={(event) => {
              event.preventDefault()
              void send()
            }}
          >
            <input
              className="login-screen__input"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="you@example.com"
              aria-label="Email address"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setError(null)
              }}
            />
            {error && <p className="login-screen__error">{error}</p>}
            <button
              type="submit"
              className="login-screen__submit"
              // Fill only once the address could actually be mailed — see isValidEmail.
              disabled={busy || !isValidEmail(email)}
            >
              Email me a code
            </button>
          </form>
        </div>
      ) : (
        <div className="login-screen__stack">
          <p className="login-screen__context">Signing in as {normalizeEmail(email)}</p>
          <form
            className="login-screen__form"
            onSubmit={(event) => {
              event.preventDefault()
              void verify(code)
            }}
          >
            {/* Six cells, ONE field. The input is the real thing — stretched across the whole row
                with its own text and caret hidden — and the cells below it just draw what it
                holds. Six inputs would mean refs, manual advance and hand-rolled backspace, and
                would break paste and the iOS SMS suggestion outright; this way all three are the
                browser's own behaviour and there is no focus code at all. */}
            <div className={`login-screen__otp${error ? ' login-screen__otp--error' : ''}`}>
              <input
                className="login-screen__otp-input"
                type="text"
                name="code"
                // What makes iOS/Android offer the code above the keyboard straight from the
                // notification, so the user never leaves the app.
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={CODE_LENGTH}
                aria-label="Six-digit sign-in code"
                value={code}
                onChange={(event) => handleCodeChange(event.target.value)}
              />
              <div className="login-screen__otp-cells" aria-hidden="true">
                {CODE_CELLS.map((index) => (
                  <div
                    key={index}
                    className={`login-screen__otp-cell${
                      index === activeCell ? ' login-screen__otp-cell--active' : ''
                    }`}
                  >
                    {code[index] ?? ''}
                  </div>
                ))}
              </div>
            </div>
            {error && <p className="login-screen__error">{error}</p>}
            <button
              type="submit"
              className="login-screen__submit"
              disabled={busy || code.length !== CODE_LENGTH}
            >
              Sign in
            </button>
          </form>

          <div className="login-screen__actions">
            <button
              type="button"
              className="login-screen__link"
              onClick={() => void send()}
              disabled={busy || cooldown > 0}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
            <button type="button" className="login-screen__link" onClick={useDifferentEmail}>
              Use a different email
            </button>
          </div>
        </div>
      )}

      {/* Both steps, one insertion point. On the code step the user has already
          continued, but the sentence is still true and a conditional would buy nothing.

          `target="_blank"` is load-bearing, not decoration: the whole sign-in state — the typed
          address, which step you are on, a half-entered code — lives in React memory on a screen
          that reloads itself on success. A same-tab navigation mid-OTP throws all of it away and
          drops the user back on an empty email field. */}
      <p className="login-screen__legal">
        By continuing you agree to the{' '}
        <a href="/terms" target="_blank" rel="noopener">
          Terms
        </a>{' '}
        and{' '}
        <a href="/privacy" target="_blank" rel="noopener">
          Privacy Policy
        </a>
        .
      </p>

      <footer className="login-screen__footer">
        <ScorelineIcon className="login-screen__footer-mark" size={FOOTER_MARK_SIZE} />
        <p className="login-screen__footer-domain">respectfootball.club</p>
      </footer>
    </div>
  )
}

export default LoginScreen
